/**
 * Valorización de las guías para el buscador.
 * Recalcula tiempos y recargos con el motor de dominio, de modo que el
 * resultado no depende de lo que se haya guardado al cargar la guía.
 */

import { useMemo } from 'react';
import { useCatalogos } from '@/hooks/useCatalogos';
import { calcularTiempos } from '@/domain/tiempos';
import { calcularRecargos, totalRecargosUf } from '@/domain/recargos';
import { generaRecargos, normalizarCodigo, parsearProducto } from '@/domain/producto';
import { roundTo } from '@/domain/precios';
import type { GuiaValorizada } from '@/domain/analisis';
import { useGuias } from '@/hooks/useGuias';

export function useGuiasValorizadas() {
  const { catalogos, buscarItem } = useCatalogos();
  const { guias, cargando } = useGuias();

  const valorizadas: GuiaValorizada[] = useMemo(() => {
    const plantaPorCodigo = new Map(catalogos.plantas.map((p) => [p.codigoPlanta, p]));
    const productoPorId = new Map(catalogos.productos.map((p) => [p.id, p]));
    const productoPorCodigo = new Map(catalogos.productos.map((p) => [p.codigo, p]));

    return guias
      .filter((g) => !g.eliminadoEn)
      .map((g) => {
        const planta = plantaPorCodigo.get(g.plantaCodigo);
        const producto =
          (g.productoId ? productoPorId.get(g.productoId) : undefined) ??
          (g.productoCodigo ? productoPorCodigo.get(g.productoCodigo) : undefined);

        const d = (s: string | null) => (s ? new Date(s) : undefined);
        const tiempos = calcularTiempos({
          salidaPlanta: d(g.tsSalidaPlanta),
          llegadaObra: d(g.tsLlegadaObra),
          inicioDescarga: d(g.tsInicioDescarga),
          terminoDescarga: d(g.tsTerminoDescarga),
          salidaObra: d(g.tsSalidaObra),
          llegadaPlanta: d(g.tsLlegadaPlanta),
        });

        // El código del producto se interpreta solo: que no esté en el
        // catálogo no es motivo para dejar la guía sin recargos.
        const codigoProd = producto?.codigo ?? g.productoCodigo;
        const parseado = codigoProd ? parsearProducto(codigoProd) : null;

        const recargos =
          planta && codigoProd && parseado && g.estado !== 'ANULADA'
            ? calcularRecargos(
                {
                  plantaCodigo: g.plantaCodigo,
                  minCargaM3: planta.minCargaM3,
                  cantidadM3: g.cantidadM3,
                  producto: parseado,
                  generaRecargos: producto
                    ? producto.generaRecargos
                    : generaRecargos(codigoProd, parseado),
                  tiempos,
                  codigoProductoNormalizado:
                    producto?.codigoNormalizado ?? normalizarCodigo(codigoProd),
                },
                buscarItem,
              )
            : [];

        const item = g.itemContratoCodigo ? buscarItem(g.itemContratoCodigo) : null;
        const precioUf = item?.precioUf ?? 0;
        const montoPrincipalUf = roundTo(g.cantidadM3 * precioUf, 4);
        const montoRecargosUf = totalRecargosUf(recargos);

        return {
          id: g.id,
          folio: g.folio,
          fecha: g.fecha,
          plantaCodigo: g.plantaCodigo,
          sector: g.sector,
          turno: g.turno,
          estado: g.estado,
          clienteNombre: g.clienteNombre,
          centroCostoCodigo: g.centroCostoCodigo,
          destinoTexto: g.destinoTexto,
          proyectoNombre: g.proyectoNombre,
          equipoCodigo: g.equipoCodigo,
          operadorMixerNombre: g.operadorMixerNombre,
          productoCodigo: producto?.codigo ?? g.productoCodigo,
          grado: parseado?.grado ?? null,
          mpa: parseado?.mpa ?? null,
          cono: parseado?.cono ?? null,
          extras: parseado?.extras ?? [],
          itemCodigo: item?.codigo ?? g.itemContratoCodigo,
          itemDescripcion: item?.descripcion ?? null,
          precioUf,
          cantidadM3: g.cantidadM3,
          montoPrincipalUf,
          recargos,
          montoRecargosUf,
          totalUf: roundTo(montoPrincipalUf + montoRecargosUf, 4),
          minEstadiaObra: tiempos.estadiaObra,
          bloquesCodelco: tiempos.bloquesCodelco,
          tiemposCompletos: tiempos.completa,
        } satisfies GuiaValorizada;
      });
  }, [guias, catalogos, buscarItem]);

  return { valorizadas, cargando, total: guias.length };
}
